import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import type { Prompt, NotificationTarget } from '../../../types/backend';
import { PromptType } from '../../../types/backend';
import { usePromptService } from '../../prompt/services/promptService';
import { useCustomerService } from '../../customer/services/customerService';
import type { FreshserviceEntity } from '../../../shared/components/FreshserviceEntityPicker';
import { automationChannel } from '../utils/ruleFormUtils';
import type { FormAction, RuleFormState } from '../utils/ruleFormUtils';
import { isWebhookTarget } from '../../../types/frontend';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import { useRunnableAgents } from '../../../shared/hooks/useRunnableAgents';
import InlineAlert from '../../../shared/components/InlineAlert';
import PromptSelector from '../../../shared/components/PromptSelector';
import { StepHeader } from '../../../shared/components/StepHeader';
import ItsmTargetCard from './targets/ItsmTargetCard';
import NotificationTargetCard from './targets/NotificationTargetCard';
import WebhookTargetCard from './targets/WebhookTargetCard';

interface RuleTargetSectionProps {
  form: RuleFormState;
  dispatch: React.Dispatch<FormAction>;
  setField: <K extends keyof RuleFormState>(field: K, value: RuleFormState[K]) => void;
  hasAttemptedSave: boolean;
  validationErrors: Record<string, string>;
  saving: boolean;
  customerId: string;
}

type ActPurpose = 'automation' | 'report';

const RuleTargetSection: React.FC<RuleTargetSectionProps> = ({
  form,
  dispatch,
  hasAttemptedSave,
  validationErrors,
  customerId,
  saving,
}) => {
  const [allPrompts, setAllPrompts] = useState<Prompt[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [groups, setGroups] = useState<FreshserviceEntity[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [agents, setAgents] = useState<FreshserviceEntity[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [customerHasItsmConfig, setCustomerHasItsmConfig] = useState<boolean | null>(null);
  const [actPurpose, setActPurpose] = useState<ActPurpose>(
    form.triggerType === 'schedule' ? 'report' : 'automation',
  );
  const promptService = usePromptService();
  const customerService = useCustomerService();

  useEffect(() => {
    if (!customerId) return;
    const load = async () => {
      setLoadingPrompts(true);
      try {
        const all = await promptService.getAllPrompts(customerId);
        setAllPrompts(all);
      } catch {
        // non-fatal
      } finally {
        setLoadingPrompts(false);
      }
    };
    load();
  }, [customerId, promptService]);

  useEffect(() => {
    if (!customerId) {
      setCustomerHasItsmConfig(null);
      return;
    }
    let cancelled = false;
    customerService
      .getCustomerById(customerId)
      .then((c) => { if (!cancelled) setCustomerHasItsmConfig(!!c.itsm_config); })
      .catch(() => { if (!cancelled) setCustomerHasItsmConfig(null); });
    return () => { cancelled = true; };
  }, [customerId, customerService]);

  // Freshservice agent groups for the rule-level group override picker.
  useEffect(() => {
    if (!customerId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    setLoadingGroups(true);
    customerService
      .listFreshserviceGroups(customerId)
      .then((g) => { if (!cancelled) setGroups(g); })
      .catch(() => { if (!cancelled) setGroups([]); })
      .finally(() => { if (!cancelled) setLoadingGroups(false); });
    return () => { cancelled = true; };
  }, [customerId, customerService]);

  // Freshservice agents for the rule-level assignee override picker.
  useEffect(() => {
    if (!customerId) {
      setAgents([]);
      return;
    }
    let cancelled = false;
    setLoadingAgents(true);
    customerService
      .listFreshserviceAgents(customerId)
      .then((a) => { if (!cancelled) setAgents(a); })
      .catch(() => { if (!cancelled) setAgents([]); })
      .finally(() => { if (!cancelled) setLoadingAgents(false); });
    return () => { cancelled = true; };
  }, [customerId, customerService]);

  // When editing an existing rule, derive the Act purpose from the selected
  // agent prompt's type once prompts load.
  useEffect(() => {
    const pid = form.automation?.prompt_id;
    if (!pid || allPrompts.length === 0) return;
    const selected = allPrompts.find((p) => p.id === pid);
    if (selected?.type === PromptType.REPORT) setActPurpose('report');
    else if (selected) setActPurpose('automation');
  }, [form.automation?.prompt_id, allPrompts]);

  const summarizeEnabled = form.summarize;
  const automationEnabled = form.automation !== null;

  const hasEmailTarget = form.notificationTargets.some(t => t.channel === 'email');
  const hasSnsTarget = form.notificationTargets.some(t => t.channel === 'sns');
  const hasWebhookTarget = form.notificationTargets.some(t => t.channel === 'webhook');
  // Webhook has no backend delivery yet — gate the entry point behind a feature switch.
  const webhookEnabled = useFeatureSwitch('webhook_target');
  // A rule names the batch agent that runs its automation; the choice only exists once a second
  // active batch agent does, so until then the rule keeps the one it has (EM-UC-22 2a).
  const agentAuthoringEnabled = useFeatureSwitch('agent_authoring');
  const { agents: batchAgents, error: batchAgentsError } = useRunnableAgents(
    'batch', agentAuthoringEnabled && automationEnabled,
  );
  const showAgentPicker = agentAuthoringEnabled && batchAgents.length > 1;

  const isEvent = form.triggerType === 'event';
  // ① "When" lives in RuleEditor; steps here start at 2. Enrich is event-only,
  // so it drops out of the numbering for schedule rules.
  const enrichStep = 2;
  const actStep = isEvent ? 3 : 2;
  const deliverStep = isEvent ? 4 : 3;

  const handlePurposeChange = (_: React.MouseEvent<HTMLElement>, next: ActPurpose | null) => {
    if (!next || next === actPurpose) return;
    setActPurpose(next);
    // Switching purpose changes which prompts are valid — clear the selection.
    dispatch({ type: 'SET_AUTOMATION', automation: automationChannel('', form.automation) });
  };

  const addNotificationTarget = (channel: NotificationTarget['channel']) => {
    dispatch({
      type: 'ADD_NOTIFICATION_TARGET',
      target: { channel, to: [], field_mappings: {} },
    });
  };

  const addWebhookTarget = () => {
    dispatch({
      type: 'ADD_NOTIFICATION_TARGET',
      target: { channel: 'webhook', url: '', headers: [], field_mappings: {} },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ② Enrich — event triggers only (no event to enrich on a schedule) */}
      {isEvent && (
        <Box>
          <StepHeader
            number={enrichStep}
            title="Understand"
            helper="AI title, summary & severity — shared by every channel"
          />
          <Accordion
            variant="outlined"
            disableGutters
            sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={summarizeEnabled ? <ExpandMoreIcon /> : null}
              sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={summarizeEnabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      dispatch({ type: 'SET_SUMMARIZE', value: e.target.checked });
                    }}
                    size="small"
                    disabled={saving}
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {summarizeEnabled ? 'Enrichment on' : 'Enrichment off'}
                  </Typography>
                }
                onClick={(e) => e.stopPropagation()}
              />
            </AccordionSummary>
            {summarizeEnabled && (
              <AccordionDetails sx={{ pt: 0 }}>
                <PromptSelector
                  prompts={allPrompts}
                  typeFilter={[PromptType.ENRICHMENT]}
                  value={form.enrichmentPromptId}
                  onChange={(id) => dispatch({ type: 'SET_ENRICHMENT_PROMPT', promptId: id ?? '' })}
                  loading={loadingPrompts}
                  label="Enrichment prompt"
                  placeholder="Default enrichment prompt"
                />
              </AccordionDetails>
            )}
          </Accordion>
        </Box>
      )}

      {/* ③ Act — run an agent: automation (investigate/remediate) or a report */}
      <Box>
        <StepHeader
          number={actStep}
          title="Act"
          helper="Agent investigates, remediates, or reports"
        />
        <Accordion
          variant="outlined"
          disableGutters
          sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={automationEnabled ? <ExpandMoreIcon /> : null}
            sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={automationEnabled}
                  onChange={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'SET_AUTOMATION', automation: e.target.checked ? automationChannel('', form.automation) : null });
                  }}
                  size="small"
                  disabled={saving}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label={<Typography variant="body2" fontWeight={600}>{automationEnabled ? 'Agent on' : 'Agent off'}</Typography>}
              onClick={(e) => e.stopPropagation()}
            />
          </AccordionSummary>
          {automationEnabled && (
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ToggleButtonGroup
                value={actPurpose}
                exclusive
                onChange={handlePurposeChange}
                size="small"
                disabled={saving}
              >
                <ToggleButton value="automation" sx={{ textTransform: 'none' }}>Automation</ToggleButton>
                <ToggleButton value="report" sx={{ textTransform: 'none' }}>Report</ToggleButton>
              </ToggleButtonGroup>
              <PromptSelector
                prompts={allPrompts}
                typeFilter={actPurpose === 'report' ? [PromptType.REPORT] : [PromptType.AUTOMATION_PROMPT]}
                value={form.automation?.prompt_id ?? null}
                onChange={(id) => dispatch({ type: 'SET_AUTOMATION', automation: automationChannel(id ?? '', form.automation) })}
                loading={loadingPrompts}
                error={hasAttemptedSave && !!validationErrors.automation_prompt_id}
                required
                label={actPurpose === 'report' ? 'Report prompt' : 'Automation prompt'}
                placeholder={actPurpose === 'report' ? 'Select a report prompt…' : 'Select an automation prompt…'}
              />
              {hasAttemptedSave && validationErrors.automation_prompt_id && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  {validationErrors.automation_prompt_id}
                </Typography>
              )}
              {agentAuthoringEnabled && batchAgentsError && (
                <InlineAlert severity="warning">
                  Could not load the agents to choose from ({batchAgentsError}). The rule keeps its agent.
                </InlineAlert>
              )}
              {showAgentPicker && form.automation && (
                <TextField
                  select
                  size="small"
                  label="Agent"
                  value={form.automation.agent_id ?? ''}
                  onChange={(e) => dispatch({
                    type: 'SET_AUTOMATION',
                    automation: { prompt_id: form.automation?.prompt_id ?? '', agent_id: e.target.value },
                  })}
                  disabled={saving}
                  helperText={batchAgents.find((a) => a.agent_id === form.automation?.agent_id)?.description}
                >
                  {batchAgents.map((a) => (
                    <MenuItem key={a.agent_id} value={a.agent_id}>{a.display_name}</MenuItem>
                  ))}
                </TextField>
              )}
            </AccordionDetails>
          )}
        </Accordion>
      </Box>

      {/* ④ Deliver to */}
      <Box>
        <StepHeader number={deliverStep} title="Deliver to" />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Create a support ticket */}
          <ItsmTargetCard
            itsm={form.itsm}
            onChange={(itsm) => dispatch({ type: 'SET_ITSM', itsm })}
            prompts={allPrompts}
            loadingPrompts={loadingPrompts}
            groups={groups}
            loadingGroups={loadingGroups}
            agents={agents}
            loadingAgents={loadingAgents}
            customerHasItsmConfig={customerHasItsmConfig}
            summarize={summarizeEnabled}
            hasAttemptedSave={hasAttemptedSave}
            validationErrors={validationErrors}
            sampleEvent={form.testEvent}
            customerId={customerId}
            disabled={saving}
          />

          {/* Notification destinations — email, SNS, webhook — in insertion order */}
          {form.notificationTargets.map((target, idx) =>
            isWebhookTarget(target) ? (
              <WebhookTargetCard
                key={`webhook-${idx}`}
                target={target}
                onChange={(updated) => dispatch({ type: 'UPDATE_NOTIFICATION_TARGET', index: idx, target: updated })}
                onRemove={() => dispatch({ type: 'REMOVE_NOTIFICATION_TARGET', index: idx })}
                hasAttemptedSave={hasAttemptedSave}
                urlError={validationErrors[`notification_${idx}_url`]}
                disabled={saving}
              />
            ) : (
              <NotificationTargetCard
                key={`${target.channel}-${idx}`}
                target={target}
                onChange={(updated) => dispatch({ type: 'UPDATE_NOTIFICATION_TARGET', index: idx, target: updated })}
                onRemove={() => dispatch({ type: 'REMOVE_NOTIFICATION_TARGET', index: idx })}
                prompts={allPrompts}
                loadingPrompts={loadingPrompts}
                summarize={summarizeEnabled}
                hasAttemptedSave={hasAttemptedSave}
                recipientError={validationErrors[`notification_${idx}_to`]}
                mappingErrors={{
                  subject: validationErrors[`notification_${idx}_mapping_subject`],
                  body: validationErrors[`notification_${idx}_mapping_body`],
                }}
                sampleEvent={form.testEvent}
                customerId={customerId}
                disabled={saving}
              />
            ),
          )}

          {/* Add notification destination buttons */}
          {(!hasEmailTarget || !hasSnsTarget || !hasWebhookTarget) && (
            <Box sx={{ display: 'flex', gap: 1, pt: 0.5, flexWrap: 'wrap' }}>
              {!hasEmailTarget && (
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addNotificationTarget('email')} disabled={saving}>
                  Email
                </Button>
              )}
              {!hasSnsTarget && (
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addNotificationTarget('sns')} disabled={saving}>
                  SNS topic
                </Button>
              )}
              {!hasWebhookTarget && webhookEnabled && (
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addWebhookTarget} disabled={saving}>
                  Webhook
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RuleTargetSection;
